<script setup lang="ts">
import { computed, h, ref, watch, nextTick } from 'vue'
import type { FunctionalComponent } from 'vue'
import { useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUIStore } from '@/stores/ui.store'
import { useFocusTrap } from '@/composables/useFocusTrap'
import clientsService from '@/services/clients.service'
import trademarksService from '@/services/trademarks.service'
import oppositionsService from '@/services/oppositions.service'
import { registryName, statusLabel } from '@/utils/caseText'
import { countryName } from '@/utils/format'

const { t } = useI18n()
const ui = useUIStore()
const router = useRouter()

const query = ref('')
const inputRef = ref<HTMLInputElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)

useFocusTrap(panelRef, () => ui.searchOpen)

watch(
  () => ui.searchOpen,
  async (open) => {
    if (open) {
      query.value = ''
      await nextTick()
      inputRef.value?.focus()
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = ''
      cancel()
    }
  }
)

/** One hit in the palette. `to` is a router target. */
interface SearchItem {
  title: string
  sub: string
  to: RouteLocationRaw
}

type GroupId = 'trademarks' | 'clients' | 'cases'

/* Until 2026-09-30 the Trademarks and Clients groups searched a built-in list
 * of the mockup's companies and marks, on the live site too: a lawyer typing
 * a real client's name found nothing, and typing "quick" found a company no
 * firm has. All three groups now ask the server, which searches only what the
 * caller may see (row-level security), so a client account's search is
 * bounded the same way its pages are:
 *
 *   trademarks  GET /brands?q=      the title and the owner's name, either script
 *   clients     GET /clients?q=     the company
 *   cases       GET /procedures?q=  the case title and number, both marks, the client
 *
 * The hits are shown as the API returned them, not re-filtered here. */
const GROUPS: { id: GroupId; label: () => string }[] = [
  { id: 'trademarks', label: () => t('nav.trademarks') },
  { id: 'clients', label: () => t('nav.clients') },
  { id: 'cases', label: () => t('opp.title') },
]

const PER_GROUP = 5
const MIN_CHARS = 2

async function findTrademarks(q: string, signal: AbortSignal): Promise<SearchItem[]> {
  const page = await trademarksService.list({ query: q, limit: PER_GROUP }, { signal })
  return page.items.slice(0, PER_GROUP).map((m) => ({
    title: m.name,
    sub: [m.regNumber, countryName(m.jurisdiction), m.owner].filter(Boolean).join(' · '),
    to: { name: 'trademark-detail', params: { id: String(m.id) } },
  }))
}

async function findClients(q: string, signal: AbortSignal): Promise<SearchItem[]> {
  const page = await clientsService.list({ query: q, limit: PER_GROUP }, { signal })
  return page.items.slice(0, PER_GROUP).map((c) => ({
    title: c.name,
    sub: [
      t('client.trademarkCount', { n: c.trademarks }, c.trademarks),
      t('client.openCaseCount', { n: c.activeCases }, c.activeCases),
    ].join(' · '),
    to: { name: 'client-detail', params: { id: String(c.id) } },
  }))
}

async function findCases(q: string, signal: AbortSignal): Promise<SearchItem[]> {
  const page = await oppositionsService.list({ q, limit: PER_GROUP }, { signal })
  return page.items.map((c) => ({
    title: c.caseNumber ? `${c.caseNumber} — ${c.title}` : c.title,
    sub: [registryName(c.registry), statusLabel(c.status), c.client.name]
      .filter((p) => p && p !== '—')
      .join(' · '),
    to: { name: 'opposition-detail', params: { id: String(c.id) } },
  }))
}

const FINDERS: Record<GroupId, (q: string, signal: AbortSignal) => Promise<SearchItem[]>> = {
  trademarks: findTrademarks,
  clients: findClients,
  cases: findCases,
}

const hits = ref<Record<GroupId, SearchItem[]>>({ trademarks: [], clients: [], cases: [] })
/** Groups still waiting for an answer to the current query. */
const pending = ref(0)
/** Groups whose request failed for the current query. */
const failed = ref(0)
/** The query the hits above answer — what the "no results" line quotes. */
const answered = ref('')

let timer: ReturnType<typeof setTimeout> | null = null
let controller: AbortController | null = null
let seq = 0

function cancel() {
  if (timer) clearTimeout(timer)
  timer = null
  controller?.abort()
  controller = null
  seq += 1
  pending.value = 0
}

/* Debounced, and each keystroke aborts the requests of the last one: a slow
 * answer to "br" must never overwrite the answer to "bra". Each group lands
 * on its own — one failing query leaves the other two groups standing rather
 * than blanking the palette (the `Promise.all` trap in @frontend). */
watch(query, (value) => {
  cancel()
  const q = value.trim()
  hits.value = { trademarks: [], clients: [], cases: [] }
  failed.value = 0
  answered.value = ''
  if (q.length < MIN_CHARS) return
  const mine = seq
  pending.value = GROUPS.length
  timer = setTimeout(() => {
    controller = new AbortController()
    const { signal } = controller
    for (const { id } of GROUPS) {
      FINDERS[id](q, signal)
        .then((items) => {
          if (mine === seq) hits.value = { ...hits.value, [id]: items }
        })
        .catch(() => {
          if (mine === seq) failed.value += 1
        })
        .finally(() => {
          if (mine !== seq) return
          pending.value -= 1
          if (pending.value === 0) answered.value = q
        })
    }
  }, 250)
})

/**
 * The title cut around the first place the query appears, for the `<em>`.
 *
 * Split into text rather than built as HTML: the titles are client and
 * trademark names from the server, and `v-html` on them would run whatever
 * markup a name carried.
 */
interface Parts {
  before: string
  hit: string
  after: string
}
function marked(text: string): Parts {
  const q = query.value.trim().toLocaleLowerCase()
  const i = q ? text.toLocaleLowerCase().indexOf(q) : -1
  if (i < 0) return { before: text, hit: '', after: '' }
  return {
    before: text.slice(0, i),
    hit: text.slice(i, i + q.length),
    after: text.slice(i + q.length),
  }
}

/* Rendered by a function, not written inline in the template: Prettier runs
 * with `htmlWhitespaceSensitivity: ignore` and puts the `<em>` on a line of
 * its own, and the whitespace that leaves splits the name — "ВЕ ТРО ГОН". */
const MarkedTitle: FunctionalComponent<{ parts: Parts }> = ({ parts }) => [
  parts.before,
  parts.hit ? h('em', parts.hit) : null,
  parts.after,
]
MarkedTitle.props = ['parts']

const results = computed(() => {
  const groups = GROUPS.filter((g) => hits.value[g.id].length).map((g) => ({
    id: g.id,
    label: g.label(),
    items: hits.value[g.id].map((item) => ({ ...item, parts: marked(item.title) })),
  }))
  return { groups, total: groups.reduce((n, g) => n + g.items.length, 0) }
})

/** Where each group's rows start in the flat keyboard order. */
const offsets = computed(() => {
  let at = 0
  return results.value.groups.map((g) => {
    const start = at
    at += g.items.length
    return start
  })
})

/** What the body says when there is no row to show. */
const message = computed(() => {
  const q = query.value.trim()
  if (q.length < MIN_CHARS) return t('search.hint')
  if (pending.value) return t('search.searching')
  if (failed.value === GROUPS.length) return t('search.failed')
  return t('search.noResults', { q: answered.value || q })
})

const activeIdx = ref(0)
watch(
  () => results.value.total,
  () => {
    activeIdx.value = 0
  }
)

const flatItems = computed(() => results.value.groups.flatMap((g) => g.items))

function open(item: SearchItem) {
  router.push(item.to)
  ui.closeSearch()
}

function onKey(e: KeyboardEvent) {
  if (!ui.searchOpen) return
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIdx.value = Math.min(flatItems.value.length - 1, activeIdx.value + 1)
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIdx.value = Math.max(0, activeIdx.value - 1)
  }
  if (e.key === 'Enter') {
    e.preventDefault()
    const item = flatItems.value[activeIdx.value]
    if (item) open(item)
  }
  if (e.key === 'Escape') ui.closeSearch()
}

/* Key caps are the names printed on the keys, not words to translate. */
const KEY = { esc: 'Esc', escShort: 'esc', mod: '⌘', k: 'K' }
/* The footer's separator, with the non-breaking spaces the mockup had. */
const SEP = '\u00a0·\u00a0'
</script>

<template>
  <div
    id="tb-search-overlay"
    class="search-overlay"
    :class="{ 'is-open': ui.searchOpen }"
    role="dialog"
    aria-modal="true"
    :aria-label="t('search.label')"
    @click.self="ui.closeSearch()"
    @keydown="onKey"
  >
    <div ref="panelRef" class="search-panel" tabindex="-1">
      <div class="search-panel__input">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input
          ref="inputRef"
          v-model="query"
          type="search"
          :placeholder="t('search.placeholder')"
          :aria-label="t('search.label')"
          autocomplete="off"
        />
        <span class="search-panel__esc">{{ KEY.esc }}</span>
      </div>

      <div class="search-panel__body">
        <template v-if="results.total">
          <div v-for="(group, gi) in results.groups" :key="group.id" class="search-group">
            <div class="search-group__head">{{ group.label }}</div>
            <a
              v-for="(item, ii) in group.items"
              :key="`${gi}-${ii}`"
              class="search-result"
              :class="{ 'is-active': activeIdx === offsets[gi] + ii }"
              href="#"
              @click.prevent="open(item)"
            >
              <span class="search-result__icon"><i class="fa-solid fa-circle-dot"></i></span>
              <div class="search-result__txt">
                <div class="search-result__title"><MarkedTitle :parts="item.parts" /></div>
                <div class="search-result__sub">{{ item.sub }}</div>
              </div>
              <i class="fa-solid fa-arrow-right search-result__chev"></i>
            </a>
          </div>
        </template>
        <div v-else class="search-empty" role="status">
          <i class="fa-regular fa-circle-question"></i>
          {{ message }}
        </div>
      </div>

      <footer class="search-panel__foot">
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd>
          {{ t('search.navigate') }} {{ SEP }}
          <kbd>↵</kbd>
          {{ t('search.open') }} {{ SEP }}
          <kbd>{{ KEY.escShort }}</kbd>
          {{ t('search.close') }}
        </span>
        <span>
          <kbd>{{ KEY.mod }}</kbd>
          <kbd>{{ KEY.k }}</kbd>
          {{ t('search.anywhere') }}
        </span>
      </footer>
    </div>
  </div>
</template>
